import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ordersApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { dateOnly } from '@/lib/format'

/**
 * Delivery or store pickup, and the date the order is to be delivered by —
 * or, for a pickup, the day the customer will collect it.
 *
 * The same date a WhatsApp order can carry and the New order form sets. It
 * is printed on the order sheet and shown on the customer's order page;
 * setting, changing or removing it here tells the customer on WhatsApp.
 * Fixed once the order is delivered or cancelled; delivery or pickup is
 * fixed once it is dispatched.
 */
export default function DeliveryDatePanel({ order, editable }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState('')
  const closed = ['delivered', 'cancelled'].includes(order.status)
  const current = order.requested_delivery_date
  const pickup = order.fulfilment === 'pickup'
  const gone = closed || ['dispatched', 'partially_delivered'].includes(order.status)
  const dateLabel = pickup ? 'Pickup date' : 'Deliver by'

  const onSaved = (updated) => {
    queryClient.setQueryData(['order', String(order.id)], updated)
    queryClient.invalidateQueries({ queryKey: ['order'] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })
  }

  const fulfil = useMutation({
    mutationFn: (value) => ordersApi.setFulfilment(order.id, value),
    onSuccess: (updated) => {
      onSaved(updated)
      setError('')
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const save = useMutation({
    mutationFn: (date) => ordersApi.setDeliveryDate(order.id, date || null),
    onSuccess: (updated) => {
      onSaved(updated)
      setEditing(false)
      setError('')
    },
    onError: (e) => setError(errorMessage(e)),
  })

  // Today in the browser's own calendar, as YYYY-MM-DD, for the picker's floor.
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10)

  return (
    <div className="delivery-date-panel">
      {error && <p className="field-error">{error}</p>}
      <div className="panel-title panel-title-row">
        {pickup ? 'Store pickup' : 'Delivery'}
        {editable && !closed && !editing && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setValue(current ?? '')
              setError('')
              setEditing(true)
            }}
          >
            {current ? 'Edit' : 'Set'}
          </button>
        )}
      </div>

      {editable && !gone ? (
        <div className="fulfilment-choice" role="radiogroup" aria-label="Delivery or store pickup">
          {[
            ['delivery', 'Delivery'],
            ['pickup', 'Store pickup'],
          ].map(([value, label]) => (
            <label key={value}>
              <input
                type="radio"
                name={`fulfilment-${order.id}`}
                checked={(pickup ? 'pickup' : 'delivery') === value}
                disabled={fulfil.isPending}
                onChange={() => fulfil.mutate(value)}
              />
              {label}
            </label>
          ))}
        </div>
      ) : (
        pickup && <p className="muted">Collected by the customer from the store.</p>
      )}
      {pickup && (
        <p className="hint">No transport service or Reference No. needed to dispatch.</p>
      )}

      {!editing && (
        <dl className="kv">
          <dt>{dateLabel}</dt>
          <dd>{current ? dateOnly(current) : <span className="muted">Not set</span>}</dd>
        </dl>
      )}

      {editing && (
        <form
          className="transport-edit"
          onSubmit={(e) => {
            e.preventDefault()
            setError('')
            save.mutate(value)
          }}
        >
          <label className="field">
            <span>{dateLabel}</span>
            <input
              type="date"
              value={value}
              min={today}
              autoFocus
              onChange={(e) => setValue(e.target.value)}
            />
          </label>
          <p className="hint">Shown on the order sheet and the customer’s order page. Saving tells the customer on WhatsApp.</p>
          <div className="transport-edit-actions">
            {current && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={save.isPending}
                onClick={() => save.mutate('')}
              >
                Remove date
              </button>
            )}
            <button type="button" className="btn btn-sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={save.isPending || !value}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
